import React, { useCallback, useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Tooltip as ReactTooltip } from 'react-tooltip';

import { LocationSearcher } from './locationSearcher';
import { FavoriteLocationButton } from './favoriteLocationButton';
import { smallMediaQuery } from '../../../common/utilities/sizeHelpers';
import { getAllFavoriteLocations, createFavoriteLocation, deleteFavoriteLocation } from '../../../common/actions/favoriteLocationActions';
import { reduxConnect } from '../../../common';
import { ArrowIcon, arrowDirections } from '../../common/utilities/arrowIcon.tsx';
import { LocationAlert } from './locationAlert';
import { FavoriteLocationDropdown } from './favoriteLocationDropdown';
import { LocationSuggestionList } from './locationSuggestionList';
import { alertRoutes, translate } from '../../../common/translation/translate';
import { useNonFocusEvent } from '../../../common/hooks/useNonFocusEvent';
import { ngDeps } from '../../../common/utilities/ngDependencies';

const tooltipInlineStyle = { width: 139, fontSize: 13, textAlign: 'center', color: '#ffffff', zIndex: 999 };
const favoriteLocationTooltipId = 'favorite-location-button-tip';

const mapStoreStateToProps = (storeState) => {
    const activeLocation = storeState?.appState?.appState?.user?.ActiveLocation || {};

    const { enableAlertsAllPageOnWeb2 } = storeState?.appState?.appState ?? {};
    const favoriteLocations = storeState?.favoriteLocations?.favoriteLocations;
    const isFavoriteLocation = favoriteLocations?.some(x => x?.Location?.Locality?.CityId === activeLocation?.Locality?.CityId);
    const { Id: activeLocationId, Locality, Station  } = activeLocation || {};
    const {
        CityId,
        CityName,
        CountryIso2Code,
        CountryIso3Code,
        CountryShortName,
        TerritoryName,
        DisplayCompositeName,
        Dma,
        GeographicCompositeName,
        Label,
        Latitude,
        Longitude,
        PostalCode,
        RouteString,
        SlugName,
        TerritoryAbbr,
        Units } = Locality || {};

    const {
        Distance,
        ElevationAboveSeaLevelMeters,
        Latitude: stationLat,
        Longitude: stationLon,
        ProviderId,
        ProviderName,
        RouteString: stationRouteStr,
        StationId,
        StationName } = Station || {};

    return {
        activeLocation,
        favoriteLocations,
        isFavoriteLocation,
        favoriteLocationsCount: favoriteLocations.length,
        activeLocationId,
        CityId,
        CityName,
        CountryIso2Code,
        CountryIso3Code,
        CountryShortName,
        TerritoryName,
        DisplayCompositeName,
        Dma,
        GeographicCompositeName,
        Label,
        Latitude,
        Longitude,
        PostalCode,
        RouteString,
        SlugName,
        TerritoryAbbr,
        Units,
        Distance,
        ElevationAboveSeaLevelMeters,
        stationLat,
        stationLon,
        ProviderId,
        ProviderName,
        stationRouteStr,
        StationId,
        StationName,
        enableAlertsAllPageOnWeb2
    };
};

const mapDispatchToProps = {
    getAllFavoriteLocations,
    createFavoriteLocation,
    deleteFavoriteLocation
};

// TECHDEBT: this will need updating when we convert the manage locations popup to React
export const LocationSelector = reduxConnect(mapStoreStateToProps, mapDispatchToProps)(({
    activeLocation,
    favoriteLocations,
    isFavoriteLocation,
    favoriteLocationsCount,
    getAllFavoriteLocations,
    createFavoriteLocation,
    deleteFavoriteLocation,
    activeLocationId,
    CityId,
    CityName,
    CountryIso2Code,
    CountryIso3Code,
    CountryShortName,
    TerritoryName,
    DisplayCompositeName,
    Dma,
    GeographicCompositeName,
    Label,
    Latitude,
    Longitude,
    PostalCode,
    RouteString,
    SlugName,
    TerritoryAbbr,
    Units,
    Distance,
    ElevationAboveSeaLevelMeters,
    stationLat,
    stationLon,
    ProviderId,
    ProviderName,
    stationRouteStr,
    StationId,
    StationName,
    enableAlertsAllPageOnWeb2
}) => {
    useEffect(()=> {
        getAllFavoriteLocations(true);
    }, []);

    //This allows for an optimistic UI when we make the API call so it appears to respond faster.
    const [ currentIsFavoriteLocation, setCurrentIsFavoriteLocation ] = useState(isFavoriteLocation);

    useEffect(() => {
        setCurrentIsFavoriteLocation(favoriteLocations?.some(x => x.Location.Locality.CityId === activeLocation?.Locality?.CityId));
    }, [activeLocation?.Locality?.CityId, favoriteLocations?.length]);

    const favoriteButtonClicked = useCallback(() => {
        // setCurrentIsFavoriteLocation(!isFavoriteLocation);
        if (isFavoriteLocation) {
            setCurrentIsFavoriteLocation(false);
            deleteFavoriteLocation(activeLocation);
            const indexOfCityToRemove = ngDeps.user.FavoriteLocations.findIndex(i => i.Location.Locality.CityId === activeLocation.Locality.CityId);
            if (indexOfCityToRemove >= 0) {
                ngDeps.user.FavoriteLocations.splice(indexOfCityToRemove, 1);
            }

        } else {
            setCurrentIsFavoriteLocation(true);
            const newFavoriteLocation = {
                Id: activeLocationId,
                Position: favoriteLocationsCount,
                Location: {
                    DefaultStationRouteString: stationRouteStr,
                    Id: activeLocationId,
                    Locality: {
                        CityId,
                        CityName,
                        CountryIso2Code,
                        CountryIso3Code,
                        CountryShortName,
                        DisplayCompositeName,
                        Dma,
                        GeographicCompositeName,
                        Label,
                        Latitude,
                        Longitude,
                        PostalCode,
                        RouteString,
                        SlugName,
                        TerritoryAbbr,
                        TerritoryName,
                        Units
                    },
                    Station: {
                        Distance,
                        ElevationAboveSeaLevelMeters,
                        Latitude: stationLat,
                        Longitude: stationLon,
                        ProviderId,
                        ProviderName,
                        RouteString: stationRouteStr,
                        StationId,
                        StationName
                    }
                }
            };

            createFavoriteLocation(newFavoriteLocation);
            ngDeps.user.FavoriteLocations.push(newFavoriteLocation);
        }
    }, [isFavoriteLocation, activeLocationId, CityId, StationId]);

    const tooltipText = currentIsFavoriteLocation ? translate('removeFavorite') : translate('saveFavorite');

    const [ showFavoriteLocationsDropdown, setShowFavoriteLocationsDropdown ] = useState(false);
    const [ showLocationSuggestionPopup, setShowLocationSuggestionPopup ] = useState(false);

    const toggleLocationSuggestionPopup = useCallback((open)=>{
        const newValue = !!open;
        if (newValue !== showLocationSuggestionPopup) {
            if (showFavoriteLocationsDropdown) {
                setShowFavoriteLocationsDropdown(false);
            }
            setShowLocationSuggestionPopup(newValue);
        }
    }, [showLocationSuggestionPopup, showFavoriteLocationsDropdown]);

    const toggleFavoriteLocationsDropdown = useCallback(()=> {
        if (showLocationSuggestionPopup) {
            setShowFavoriteLocationsDropdown(false);
        }
        setShowFavoriteLocationsDropdown(!showFavoriteLocationsDropdown);
    }, [showLocationSuggestionPopup, showFavoriteLocationsDropdown]);

    const containerRef = useNonFocusEvent(() => setShowFavoriteLocationsDropdown(false));

    const targetProps = enableAlertsAllPageOnWeb2 ? { target: '_self' } : { };

    return (
        <Container ref={containerRef}>
            <span data-tooltip-id={favoriteLocationTooltipId}>
                <FavoriteLocationButton isFavoriteLocation={currentIsFavoriteLocation} onClick={favoriteButtonClicked} />
            </span>
            <ReactTooltip 
                style={tooltipInlineStyle} 
                positionStrategy='fixed' 
                place='bottom' 
                effect='solid'  
                id={favoriteLocationTooltipId} 
                content={tooltipText} />
            <LocationSearcher toggleSuggestionPopup={toggleLocationSuggestionPopup} />
            <ArrowContainer id='favoritesDropdownArrowButton' onClick={toggleFavoriteLocationsDropdown}>
                <ArrowIcon arrowDirection={showFavoriteLocationsDropdown ? arrowDirections.up : arrowDirections.down} />
            </ArrowContainer>
            <AlertContainer>
                <LocationAlertLink href={alertRoutes.allAlerts} {...targetProps} rel='noreferrer' aria-label={translate('severeWeatherAlerts')}>
                    <LocationAlert
                        latitude={Latitude}
                        longitude={Longitude}
                        emLocationId={activeLocationId}
                    />
                </LocationAlertLink>
            </AlertContainer>
            {showFavoriteLocationsDropdown && <FavoriteLocationDropdown close={() => setShowFavoriteLocationsDropdown(false)} />}
            { showLocationSuggestionPopup && <LocationSuggestionList /> }
        </Container>
    );
});

LocationSelector.displayName = 'LocationSelector';

LocationSelector.propTypes = {
    getAllFavoriteLocations: PropTypes.func
};

const Container = styled.div`
    border-left: 1px solid #d2d6d9;
    color: #435560;
    display: flex;
    height: 54px;
    align-items: center;
    position: relative;
    @media ${smallMediaQuery} {
        flex: 1;
        border-left: none;
    }
`;

const ArrowContainer = styled.div`
    padding-left: 16px;
    padding-right: 16px;
    cursor: pointer;
    height: 56px;
    display: flex;
    align-items: center;
    margin-left: 4px;
    &:hover {
        background-color: rgba(47,111,140,0.05)
    }
    @media${smallMediaQuery} {
        padding-left: 12px;
        padding-right: 12px;
    }
`;

const AlertContainer = styled.div`
    height: 54px;
    border-left: 1px solid #d2d6d9;
    display: flex;
    align-items: center;
    @media${smallMediaQuery} {
        border-left: none;
    }
`;

const LocationAlertLink = styled.a`
    height: 100%;
    display: flex;
    align-items: center;
    &:hover {
        background-color: rgba(47,111,140,0.05)
    }
`;
